import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Rocket, Quote, ExternalLink, Heart, Mail, Send, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InitiativesPage = () => {
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newsletterForm, setNewsletterForm] = useState({ name: '', email: '' });
  const [newsletterStatus, setNewsletterStatus] = useState('idle');
  const [newsletterError, setNewsletterError] = useState('');

  useEffect(() => {
    const fetchInitiatives = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/initiatives`);
        setInitiatives(response.data);
      } catch (err) {
        console.error('Error fetching initiatives:', err);
        setInitiatives([{
          id: 'default-jessica',
          title: 'Send Jessica To Space',
          subtitle: 'The First Openly Autistic Woman In Space',
          image: 'https://customer-assets.emergentagent.com/job_vulcan-mirror/artifacts/86qa6q9q_image2.jpeg',
          person_quote: "I could not be more thrilled, honoured, and excited to be working with the Nimoy-Knight foundation to become the first openly autistic woman in space. Going to space is a dream come true, but being chosen to represent what Leonard stood for is even more incredible.",
          person_name: 'Jessica Schonhut-Stasik',
          foundation_quote: "Nimoy Knight is proud to announce we're officially supporting Jessica's dream to be the first autistic woman to go into space.",
          is_active: true
        }]);
      } finally {
        setLoading(false);
      }
    };
    fetchInitiatives();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus('loading');
    setNewsletterError('');

    try {
      await axios.post(`${API_URL}/api/newsletter-signups`, newsletterForm);
      setNewsletterStatus('success');
      setNewsletterForm({ name: '', email: '' });
    } catch (err) {
      setNewsletterStatus('error');
      setNewsletterError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center p-3 bg-amber-500/10 rounded-xl mb-6">
            <Rocket className="w-8 h-8 text-amber-500" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            Live Long And Prosper
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            The Nimoy Knight Foundation is proud to support world-changing people and organizations.
          </p>
        </div>
      </section>

      {/* Initiatives Section */}
      <section className="py-16 bg-[#0a0a0b]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
            </div>
          ) : (
            <div className="space-y-16">
              {initiatives.map((initiative, index) => (
                <div key={initiative.id}>
                  <div className="bg-[#141416] border border-gray-800/50 rounded-3xl overflow-hidden">
                    <div className="flex flex-col lg:flex-row">
                      {/* Image */}
                      {initiative.image && (
                        <div className="lg:w-2/5">
                          <img
                            src={initiative.image}
                            alt={initiative.title}
                            className="w-full h-80 lg:h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className={`${initiative.image ? 'lg:w-3/5' : 'w-full'} p-8 lg:p-12 flex flex-col justify-center`}>
                        {/* Title */}
                        <div className="mb-6">
                          <h2 className="text-3xl font-bold text-white mb-2">
                            {initiative.title}
                          </h2>
                          {initiative.subtitle && (
                            <p className="text-amber-500 text-xl font-medium">
                              {initiative.subtitle}
                            </p>
                          )}
                        </div>

                        {/* Person Quote */}
                        {initiative.person_quote && (
                          <div className="mb-6 relative">
                            <Quote className="w-8 h-8 text-amber-500/30 absolute -top-2 -left-2" />
                            <blockquote className="pl-6 border-l-2 border-amber-500/50">
                              <p className="text-gray-300 italic leading-relaxed">
                                "{initiative.person_quote}"
                              </p>
                              {initiative.person_name && (
                                <cite className="text-amber-500 font-medium mt-2 block not-italic">
                                  — {initiative.person_name}
                                </cite>
                              )}
                            </blockquote>
                          </div>
                        )}

                        {/* Foundation Quote */}
                        {initiative.foundation_quote && (
                          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-6 mb-6">
                            <p className="text-white font-medium">
                              {initiative.foundation_quote}
                            </p>
                            <p className="text-amber-500 text-sm mt-2">
                              — The Nimoy Knight Foundation
                            </p>
                          </div>
                        )}

                        {/* Description */}
                        {initiative.description && (
                          <p className="text-gray-400 mb-6 leading-relaxed">
                            {initiative.description}
                          </p>
                        )}

                        {/* Link */}
                        {initiative.link_url && (
                          <a
                            href={initiative.link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all w-fit"
                          >
                            {initiative.link_text || 'Learn More'}
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Donate Button - After each initiative */}
                  <div className="mt-8 text-center">
                    <p className="text-gray-400 mb-4">Support this initiative and help make dreams come true</p>
                    <Link
                      to="/donate"
                      className="inline-flex items-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-full transition-all hover:scale-105"
                    >
                      <Heart className="w-5 h-5" />
                      Donate to Support
                    </Link>
                  </div>
                </div>
              ))}

              {initiatives.length === 0 && !loading && (
                <div className="text-center py-12">
                  <p className="text-gray-500">No initiatives to display at this time.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup Section */}
      <section className="py-20 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#141416] border border-amber-500/20 rounded-3xl p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center p-3 bg-amber-500/10 rounded-full mb-4">
                <Mail className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">
                Stay Connected
              </h2>
              <p className="text-gray-300 text-lg max-w-xl mx-auto">
                To be the first to receive news and updates from our newsletter, sign up today!
              </p>
            </div>

            {newsletterStatus === 'success' ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">You're Subscribed!</h3>
                <p className="text-gray-300">
                  Thank you for signing up. You'll receive our latest news and updates.
                </p>
                <button
                  onClick={() => setNewsletterStatus('idle')}
                  className="mt-6 text-amber-500 hover:text-amber-400 underline"
                >
                  Sign up another person
                </button>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto space-y-4">
                <div>
                  <label htmlFor="newsletter-name" className="block text-gray-400 text-sm mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="newsletter-name"
                    value={newsletterForm.name}
                    onChange={(e) => setNewsletterForm({ ...newsletterForm, name: e.target.value })}
                    required
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors"
                    data-testid="newsletter-name-input"
                  />
                </div>
                
                <div>
                  <label htmlFor="newsletter-email" className="block text-gray-400 text-sm mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="newsletter-email"
                    value={newsletterForm.email}
                    onChange={(e) => setNewsletterForm({ ...newsletterForm, email: e.target.value })}
                    required
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors"
                    data-testid="newsletter-email-input"
                  />
                </div>

                {newsletterStatus === 'error' && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                    {newsletterError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={newsletterStatus === 'loading'}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 text-black font-bold rounded-xl transition-all hover:scale-[1.02] disabled:hover:scale-100"
                  data-testid="newsletter-submit-btn"
                >
                  {newsletterStatus === 'loading' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Subscribing...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Subscribe to Newsletter
                    </>
                  )}
                </button>

                <p className="text-center text-gray-500 text-xs mt-4">
                  🖖 Live Long and Prosper
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0a0a0b]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Have an Initiative to Propose?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            We're always looking for world-changing ideas that align with Leonard Nimoy's values of hope, compassion, and celebrating neurodiversity.
          </p>
          <a
            href="mailto:info@rememberingleonardnimoy.org?subject=Initiative Proposal"
            className="inline-flex items-center justify-center px-8 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all hover:scale-105"
          >
            Contact Us
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InitiativesPage;
